import { GTMProvider } from "@elgorditosalsero/react-gtm-hook";
import { ISnippetsParams } from "@elgorditosalsero/react-gtm-hook/dist/models/GoogleTagManager";
import { Scitylana } from "@smash-sdk-private/scitylana/11-2021";
import { config, HttpResponse, RefreshTokenMethod } from '@smash-sdk/core';
import { errors, Iam } from "@smash-sdk/iam/10-2019";
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import pkg from '../package.json';
import DashboardLayout from './components/DashboardLayout/DashboardLayout';
import MainLayout from './components/MainLayout/MainLayout';
import SmashSpinner from './components/SmashSpinner/SmashSpinner';
import PrivateRoute from './features/auth/components/PrivateRoute';
import PublicRoute from './features/auth/components/PublicRoute';
import { DisplayUser } from "./features/auth/models/DisplayUser.interface";
import authService from "./features/auth/services/auth.service";
import { useDiscovery } from './hooks/discovery/useDiscovery';
import { useIdentity } from './hooks/identity/useIdentity';
import ApiKeys from './pages/Private/ApiKeys';
import Billing from './pages/Private/Billing';
import DomainDetails from './pages/Private/DomainDetails';
import Domains from './pages/Private/Domains';
import Overview from './pages/Private/Overview';
import Profile from './pages/Private/Profile';
import Transfers from './pages/Private/Transfers';
import Usage from './pages/Private/Usage';
import Login from './pages/Public/Login';
import Signup from './pages/Public/Signup';
import { buildScitylanaEvent } from './shared/helpers/helpers';
import TransferDetails from "./pages/Private/TransferDetails";
import { logout } from "./features/auth/authSlice";
import { useAppDispatch } from "./hooks/redux/hooks";
import DocumentationIntroduction from "./pages/Public/DocumentationIntroduction";
import APIReferenceService from "./pages/Public/APIReferenceService";
import DocumentationLayout from "./components/DocumentationLayout/DocumentationLayout";
import { useApiConfiguration } from "./hooks/ApiConfiguration/useApiConfiguration";
import APIReferenceGetStarted from "./pages/Public/APIReferenceGetStarted";

function App() {

    const location = useLocation()
    const [, identity] = useIdentity();
    const [apiConfiguration] = useApiConfiguration()
    const [isLoading, isSuccess] = useDiscovery();
    const dispatch = useAppDispatch();

    const gtmParams: ISnippetsParams = {
        id: process.env.REACT_APP_GTM_ID as string,
        environment: {
            gtm_auth: process.env.REACT_APP_GTM_AUTH as string,
            gtm_preview: process.env.REACT_APP_GTM_PREVIEW as string,
        },
    };

    const refreshTokenMethod: RefreshTokenMethod = (httpResponse: HttpResponse<any>, retries?: number) => {
        return new Promise(async (resolve, reject) => {
            try {
                if (retries !== undefined && retries < parseInt(process.env.REACT_APP_SDK_MAX_RETRIES as string)) {
                    const refreshToken = (identity as DisplayUser).refreshToken.token;
                    const response = await new Iam().renewToken({ refreshToken });
                    authService.setTokens(response.identity);//TODO rename? dispatch instead  
                    resolve(response.identity.token.token);
                } else {
                    dispatch(logout());
                    resolve();
                }
            } catch (error: unknown) {
                if (error instanceof errors.RenewTokenError.BadGatewayError
                    || error instanceof errors.RenewTokenError.GatewayTimeoutError
                    || error instanceof errors.RenewTokenError.NetworkError
                    || error instanceof errors.RenewTokenError.UnknownError
                    || error instanceof errors.RenewTokenError.InternalServerError) {
                    const token = (identity as DisplayUser).token.token;
                    resolve(token);
                } else if (error instanceof errors.RenewTokenError.IdentityDisabledError
                    || error instanceof errors.RenewTokenError.InvalidParameterError
                    || error instanceof errors.RenewTokenError.InvalidRefreshTokenError
                    || error instanceof errors.RenewTokenError.NotAllowedError//TODO FIX ME this error change in the next verison of iam
                    || error instanceof errors.RenewTokenError.PasswordRevokedError
                    || error instanceof errors.RenewTokenError.NotFoundError) {
                    dispatch(logout());
                    resolve();
                } else {
                    dispatch(logout());
                    reject(error);
                }
            }
        });
    }

    useEffect(() => {
        console.log(`Version ${pkg.version}`);
        config.setRefreshTokenMethod(refreshTokenMethod);
    }, []);

    useEffect(() => {
        if (isSuccess && identity) {
            const scitylana = new Scitylana();
            const event = buildScitylanaEvent(location);
            scitylana.createTneve(event);
        }
    }, [location, isSuccess, identity]);

    if (isLoading || !isSuccess || !apiConfiguration) {
        return (
            <div className="vh-100">
                <div className="h-100 d-flex d-column justify-content-center align-items-center">
                    <SmashSpinner />
                </div>
            </div>
        );
    }

    return (
        <>
            <GTMProvider state={gtmParams}>
                <Routes>
                    <Route element={<PublicRoute page={<MainLayout />} />}>
                        {/* <Route index element={<LandingPage />} />
                            <Route path='/faq' element={<FAQ />} /> */}
                        <Route path='/signup' element={<Signup />} />
                        <Route path='/login' element={<Login />} />
                        <Route path='*' element={<Navigate to='/' />} />
                    </Route>
                    <Route element={<PublicRoute page={<DocumentationLayout apiConfiguration={apiConfiguration} />} accessibleWhenLoggedIn={true} />}>
                        <Route path='/docs' element={<DocumentationIntroduction />} />
                        <Route path='/docs/APIReference' element={<APIReferenceGetStarted />} />
                        <Route path='/docs/APIReference/:service' element={<APIReferenceService />} />
                    </Route>
                    <Route element={<PrivateRoute page={<DashboardLayout />} />}>
                        <Route index element={<Overview />} />
                        <Route path='/apikeys' element={<ApiKeys />} />
                        <Route path='/transfers' element={<Transfers />} />
                        <Route path='/transfers/:id' element={<TransferDetails />} />
                        <Route path='/settings/domains' element={<Domains />} />
                        <Route path='/settings/domains/:id' element={<DomainDetails />} />
                        <Route path='/profile' element={<Profile />} />
                        <Route path='/billing' element={<Billing />} />
                        <Route path='/reports' element={<Usage />} />
                    </Route>
                </Routes>
            </ GTMProvider>
        </>
    )
}

export default App;
